import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Cookie } from "lucide-react";
import { notifyConsentChanged, OPEN_COOKIE_EVENT } from "@/lib/consent";

// Standalone cookie-consent banner for the public landing/directory pages.
// Shares config (GET /public/popups) and the localStorage key with the
// storefront banner, so a shopper only consents once across the whole site.
export default function CookieConsent() {
  const [cfg, setCfg] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    api.get("/public/popups").then((r) => {
      const c = r.data?.cookie;
      setCfg(c);
      if (c?.enabled && localStorage.getItem("sig_cookie_consent") == null) setShow(true);
    }).catch((e) => { console.debug("cookie consent: config load failed", e); });
  }, []);

  useEffect(() => {
    const open = () => setShow(true);
    window.addEventListener(OPEN_COOKIE_EVENT, open);
    return () => window.removeEventListener(OPEN_COOKIE_EVENT, open);
  }, []);

  const choose = (val) => { localStorage.setItem("sig_cookie_consent", val); setShow(false); notifyConsentChanged(); };

  if (!cfg) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-[90] bg-white border border-zinc-200 shadow-2xl rounded-2xl p-5"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          data-testid="cookie-popup"
        >
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-zinc-100 flex items-center justify-center shrink-0"><Cookie size={18} /></div>
            <p className="font-body text-sm text-zinc-600 leading-relaxed" data-testid="cookie-message">{cfg.message}</p>
          </div>
          <div className="flex items-center gap-2 mt-4">
            <button onClick={() => choose("declined")} data-testid="cookie-decline"
              className="flex-1 text-sm font-semibold border border-zinc-200 rounded-full py-2.5 hover:bg-zinc-50">{cfg.decline_label || "Decline"}</button>
            <button onClick={() => choose("accepted")} data-testid="cookie-accept"
              className="flex-1 text-sm font-semibold text-white bg-black rounded-full py-2.5 active:scale-95 transition-transform">{cfg.accept_label || "Accept"}</button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
